import axios from "axios";
import {
  PickDateBtn,
  SearchBar,
  StatisticsDashboardCard,
  SyncProfileButton,
} from "./Components/molecules";
import {
  PostBarGraphArea,
  PostCalendarArea,
  PostPerformanceSkeleton,
  PostPerformanceTable,
} from "./Components/organisms";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../Providers/Auth";
import { Button } from "../../../../shadcnComponents/ui/button";
import { HiLightBulb } from "react-icons/hi";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../shadcnComponents/ui/tooltip";
import { InfoIcon } from "lucide-react";
import { useInfluencer } from "../../../../Providers/Influencer";
import { useToast } from "../../../../shadcnComponents/ui/use-toast";
import {
  ExtensionCheckDialog,
  InstallExtensionDialogContainer,
} from "../../../Influencer/CreateProfileV1/SyncLinkedinWithExt";
import { BiSync } from "react-icons/bi";
import React from "react";
import { DateRange } from "react-day-picker";
import mixpanel from "mixpanel-browser";

const host = import.meta.env.VITE_BACKEND_SERVER_URL;

export const PostPerformance = () => {
  const authState = useAuth();
  const influencerState = useInfluencer();
  const { toast } = useToast();
  const [extData, setExtData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  // const [filteredPosts, setFilteredPosts] = useState([]);
  const [metricsData, setMetricsData] = useState(null);
  const [openExtInstall, setOpenExtInstall] = useState(false);
  const [tableDateRange, setTableDateRange] = React.useState<
    DateRange | undefined
  >({
    from: null,
    to: null,
  });

  useEffect(() => {
    mixpanel.track("Post Performance Page Viewed");

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("jwtToken");

        const [linkedinRes, influencerRes] = await Promise.all([
          axios.get(`${host}/ext/linkedin/me`, {
            headers: { jwtToken: token },
          }),
          influencerState.getTempInfluencerLinkedinProfileFromSlug(
            authState?.loggedUser?.slug
          ),
        ]);

        if (linkedinRes?.data?.success) {
          const posts = linkedinRes.data.data.posts;
          setExtData(linkedinRes.data.data);

          if (influencerRes.success && influencerRes?.LinkedinAnalysis?.name) {
            setMetricsData({
              ...influencerRes.LinkedinAnalysis,
              totalPosts: posts.length,
              avgImpressions: calculateMetrics(posts)?.avgImpressions,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setTimeout(() => setLoading(false), 1000);
      }
    };

    fetchData();
  }, [authState?.loggedUser, influencerState]);

  const filteredPosts = useMemo(() => {
    if (!extData?.posts) return [];
    const { from, to } = tableDateRange || {};

    const filtered = extData?.posts
      .filter((post) => {
        if (from && to) {
          const postDate = new Date(post?.postCreatedOn);
          return postDate >= from && postDate <= to;
        }
        return true;
      })
      .filter((post) =>
        post?.postDescription
          ?.toLowerCase()
          .includes(searchKeyword.toLowerCase())
      );

    if ((from && to) || searchKeyword) {
      setMetricsData((prev) => {
        return {
          ...prev,
          ...calculateMetrics(filtered),
        };
      });
    }

    return filtered;
  }, [extData?.posts, tableDateRange, searchKeyword]);

  const handleSearchPost = useCallback((e) => {
    setSearchKeyword(e.target.value);
  }, []);

  function handleSyncProfile() {
    if (window.innerWidth <= 768) {
      // Mobile screen, show a toast message
      toast({
        title: "Please switch to a desktop to sync",
        variant: "destructive",
      });
    } else {
      // Desktop screen, execute the function
      setOpenExtInstall(true);
    }
  }

  return (
    <>
      {openExtInstall && (
        <ExtensionCheckDialog
          open={openExtInstall}
          onOpenChange={setOpenExtInstall}
          content={
            <InstallExtensionDialogContainer
              reSync={true}
              redirectUrl={`${window.location.origin}/influencer/plugin/callback`}
              closeCurrentWindow={true}
              open={openExtInstall}
            />
          }
        />
      )}
      {loading && <PostPerformanceSkeleton />}
      {extData?.posts?.length === 0 && (
        <div className="h-[80vh] text-white flex items-center justify-center px-3">
          <div className="flex flex-col justify-center items-center gap-5 border border-muted/20 rounded-md p-10">
            <img src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/image+54.png" />
            <div className="text-white/70 text-sm text-center">
              No data found.
              <br />
              Please sync your LinkedIn profile to view the statistics.
            </div>
            <Button
              variant="destructive"
              className="py-2 px-10 hover:bg-red-800 rounded-md border border-red-800 text-xs md:text-sm"
              onClick={handleSyncProfile}
            >
              <span className="text-white font-inter">Sync Profile</span>
              <BiSync size={20} title="sync" className="" />{" "}
            </Button>
          </div>
        </div>
      )}
      {!loading && extData?.posts?.length > 0 && (
        <div className="w-full relative  h-max items-start flex p-[16px] md:p-[40px]  pb-[100px] md:pb-[16px] gap-[15px]">
          <div className="flex flex-col text-white gap-9 w-full relative  pb-[100px]">
            <div className="flex flex-col md:flex-row gap-2 md:gap-6 w-full justify-between rounded-[8px] border-[1px] border-solid border-[#262626]">
              <PostBarGraphArea
                firstThirtyComments={metricsData?.firstThirtyComments}
                firstThirtyLikes={metricsData?.firstThirtyLikes}
                posts={extData?.posts}
              />
              <div className="py-4 grid place-items-center">
                <div className="h-[1px] w-full md:h-[100%] md:w-[1px] bg-[#292F35]"></div>
              </div>
              <PostCalendarArea posts={extData?.posts} />
            </div>
            <div>
              <div className="w-full justify-between flex items-center gap-5 mb-3">
                <h3 className="text-center font-[Inter] text-base not-italic font-semibold leading-[16px]">
                  Filter as required
                </h3>
                <PickDateBtn
                  date={tableDateRange}
                  setDate={setTableDateRange}
                />
              </div>
              <div className="rounded-md border border-[#292F35] p-4">
                <StatisticsDashboardCard
                  MetricData={[
                    {
                      title: "Total Post",
                      value:
                        metricsData?.totalPosts > 0
                          ? metricsData?.totalPosts
                          : "-",
                      active: true,
                    },
                    {
                      title: "Average Likes",
                      value:
                        metricsData?.avgLikes > 0
                          ? Math.ceil(metricsData?.avgLikes)
                          : "-",
                    },
                    {
                      title: "Average Comments",
                      value:
                        metricsData?.avgComments > 0
                          ? Math.ceil(metricsData?.avgComments)
                          : "-",
                    },
                    {
                      title: "Average Impressions",
                      value:
                        metricsData?.avgImpressions > 0
                          ? Math.ceil(metricsData?.avgImpressions)
                          : "-",
                    },
                    {
                      title: "Average Engagements",
                      value:
                        metricsData?.avgEngagementPercent > 0
                          ? Math.ceil(metricsData?.avgEngagementPercent * 10) /
                            10
                          : "-",
                      isPercentage: true,
                    },
                    {
                      title: "Followers",
                      value:
                        metricsData?.followers > 0
                          ? metricsData?.followers
                          : "-",
                    },
                  ]}
                />
                <div className="flex justify-between items-center gap-5 my-3">
                  <div className="flex flex-col gap-1.5">
                    <p className="font-[Inter] text-sm not-italic font-medium leading-[20px]">
                      Search Posts by Title
                    </p>
                    <SearchBar
                      value={searchKeyword}
                      onValueChange={setSearchKeyword}
                      onSearch={() => handleSearchPost}
                    />
                  </div>
                  <SyncProfileButton
                    handleSyncProfile={handleSyncProfile}
                    updatedAt={extData?.profile?.updatedAt}
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <span className="text-[#A6A6A6] font-[Inter] text-sm not-italic font-medium leading-[24px] flex gap-1 items-center">
                    <span className="w-fit">
                      <HiLightBulb
                        size={20}
                        color="#F1F1F1"
                        className="shrink-0"
                      />
                    </span>
                    <span className="w-fit">
                      Here are your LinkedIn stats at a glance to help you
                      create content, better.
                      <span className="text-white">
                        {" "}
                        Sync again, if something is incorrect.
                      </span>
                    </span>
                    <span className="w-fit">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <InfoIcon
                              size={16}
                              color="#F1F1F1"
                              className="shrink-0"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="bg-white border ">
                            <p className="w-[300px] text-xs text-center">
                              Errors can arise due to slow internet, switching
                              tabs while syncing, or delay in response via
                              LinkedIn
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>
                  </span>
                  <div>
                    <PostPerformanceTable
                      postData={{
                        posts: filteredPosts,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const calculateMetrics = (posts) => {
  const totalPosts = posts.length;

  const avgLikes =
    posts.reduce((acc, post) => acc + (post.likes || 0), 0) / posts.length;

  const avgComments =
    posts.reduce((acc, post) => acc + (post.comments || 0), 0) / posts.length;

  const postCountWithImpressions = posts.filter(
    (post) => post.impressions > 0
  ).length;

  const avgImpressions =
    posts.reduce((acc, post) => acc + (post.impressions || 0), 0) /
    (postCountWithImpressions || posts.length || 1);

  const avgEngagementPercent =
    posts.reduce((acc, post) => acc + (post.engagement || 0), 0) / posts.length;

  return {
    totalPosts,
    avgLikes,
    avgComments,
    avgImpressions,
    avgEngagementPercent,
  };
};
